import { useEffect, useState } from "react";
import { Loader2, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";

const VEHICLE_KEYS = ["standard", "large_suv", "small_van", "medium_van", "large_van", "other"];
const CONDITION_KEYS = ["runner", "non_runner_rolling", "non_runner_seized"];
const LOWERED_KEYS = ["no", "yes", "not_sure"];
const URGENCY_KEYS = ["planned", "next_day", "same_day"];
const TIME_KEYS = ["flexible", "day", "early", "evening", "late", "overnight"];

const LABELS = {
  standard: "Standard car",
  large_suv: "Large SUV / 4x4",
  small_van: "Small van",
  medium_van: "Medium van",
  large_van: "Large van",
  other: "Other (manual quote)",
  runner: "Runner",
  non_runner_rolling: "Non-runner — rolling",
  non_runner_seized: "Non-runner — seized (manual)",
  no: "Not lowered",
  yes: "Lowered",
  not_sure: "Not sure (flag only)",
  planned: "Planned (2+ days)",
  next_day: "Next day",
  same_day: "Same day",
  flexible: "Flexible / any time",
  day: "08:00 – 18:00",
  early: "05:00 – 08:00",
  evening: "18:00 – 21:00",
  late: "21:00 – 00:00",
  overnight: "00:00 – 05:00",
};

function numOrNull(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

export default function PricingSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [s, setS] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/settings/pricing");
      setS(data);
    } catch (e) {
      console.error(e);
      toast.error("Could not load pricing settings.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function save() {
    if (!s) return;
    setSaving(true);
    try {
      const { data } = await apiClient.put("/admin/settings/pricing", s);
      setS(data);
      toast.success("Pricing updated.");
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Save failed.";
      toast.error(typeof msg === "string" ? msg : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  if (loading || !s) {
    return (
      <div className="border border-white/10 bg-[#0A0A0A] p-16 text-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#E60000] mx-auto" />
      </div>
    );
  }

  const updateBand = (i, key, value) => {
    const bands = [...s.mileage_bands];
    bands[i] = { ...bands[i], [key]: numOrNull(value) };
    setS({ ...s, mileage_bands: bands });
  };

  const setPath = (obj, key, value) =>
    setS({ ...s, [obj]: { ...s[obj], [key]: numOrNull(value) } });

  return (
    <div className="space-y-8" data-testid="admin-pricing">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-xs text-zinc-500 max-w-xl">
          Editable pricing engine. Set a value to <code>blank</code> to mark an option
          as a manual/custom quote (only for vehicle &ldquo;Other&rdquo; and seized vehicles).
          Percentages are additive — same-day +20% and overnight +35% combine to +55%.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            data-testid="admin-pricing-reset"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-xs hover:border-white/40 transition-colors uppercase tracking-widest font-bold"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="admin-pricing-save"
            className="btn-primary disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save changes
          </button>
        </div>
      </div>

      {/* Fixed charges */}
      <Card title="Fixed charges">
        <Grid>
          <NumberField
            label="Call-out charge (£)"
            value={s.callout_charge}
            onChange={(v) => setS({ ...s, callout_charge: numOrNull(v) })}
            testId="setting-callout"
          />
          <NumberField
            label="Minimum charge (£)"
            value={s.minimum_charge}
            onChange={(v) => setS({ ...s, minimum_charge: numOrNull(v) })}
            testId="setting-min"
          />
          <NumberField
            label="Waiting rate (£ / 30 min)"
            value={s.waiting_charge_per_30min}
            onChange={(v) => setS({ ...s, waiting_charge_per_30min: numOrNull(v) })}
            testId="setting-waiting-rate"
          />
          <NumberField
            label="Waiting included (mins)"
            value={s.waiting_included_minutes}
            onChange={(v) => setS({ ...s, waiting_included_minutes: numOrNull(v) })}
            testId="setting-waiting-mins"
          />
          <NumberField
            label="Quote validity (hours)"
            value={s.quote_validity_hours}
            onChange={(v) => setS({ ...s, quote_validity_hours: numOrNull(v) })}
            testId="setting-validity"
          />
        </Grid>
      </Card>

      {/* Mileage bands */}
      <Card title="Mileage bands (non-progressive — one rate applies to the whole journey)">
        <div className="space-y-3">
          {s.mileage_bands.map((b, i) => (
            <div
              key={i}
              className="grid grid-cols-1 sm:grid-cols-3 gap-3 border border-white/10 bg-[#050505] p-4"
              data-testid={`setting-band-${i}`}
            >
              <div>
                <MiniLabel>From</MiniLabel>
                <div className="text-white text-sm">
                  {i === 0 ? 0 : (s.mileage_bands[i - 1].upto ?? 0) + 1} mi
                </div>
              </div>
              <NumberField
                label="Up to (miles)"
                placeholder="Leave blank for unlimited"
                value={b.upto ?? ""}
                onChange={(v) => updateBand(i, "upto", v)}
                testId={`setting-band-${i}-upto`}
              />
              <NumberField
                label="Rate (£ / mile)"
                step="0.01"
                value={b.rate}
                onChange={(v) => updateBand(i, "rate", v)}
                testId={`setting-band-${i}-rate`}
              />
            </div>
          ))}
        </div>
      </Card>

      {/* Vehicle adjustments */}
      <Card title="Vehicle-size adjustment (£)">
        <Grid>
          {VEHICLE_KEYS.map((k) => (
            <NumberField
              key={k}
              label={LABELS[k]}
              placeholder={k === "other" ? "blank = manual" : "0"}
              value={s.vehicle_adjustments[k] ?? ""}
              onChange={(v) => setPath("vehicle_adjustments", k, v)}
              testId={`setting-vehicle-${k}`}
            />
          ))}
        </Grid>
      </Card>

      {/* Condition & lowered */}
      <Card title="Condition adjustment (£)">
        <Grid>
          {CONDITION_KEYS.map((k) => (
            <NumberField
              key={k}
              label={LABELS[k]}
              placeholder={k.endsWith("seized") ? "blank = manual" : "0"}
              value={s.condition_adjustments[k] ?? ""}
              onChange={(v) => setPath("condition_adjustments", k, v)}
              testId={`setting-condition-${k}`}
            />
          ))}
        </Grid>
      </Card>

      <Card title="Lowered adjustment (£)">
        <Grid>
          {LOWERED_KEYS.map((k) => (
            <NumberField
              key={k}
              label={LABELS[k]}
              value={s.lowered_adjustments[k] ?? ""}
              onChange={(v) => setPath("lowered_adjustments", k, v)}
              testId={`setting-lowered-${k}`}
            />
          ))}
        </Grid>
      </Card>

      {/* Urgency + Time percentages */}
      <Card title="Urgency uplift (%) — based on collection date vs today">
        <Grid>
          {URGENCY_KEYS.map((k) => (
            <NumberField
              key={k}
              label={LABELS[k]}
              value={s.urgency_pct[k] ?? 0}
              onChange={(v) => setPath("urgency_pct", k, v)}
              testId={`setting-urgency-${k}`}
              suffix="%"
            />
          ))}
        </Grid>
      </Card>

      <Card title="Time-of-day uplift (%) — added to urgency, not compounded">
        <Grid>
          {TIME_KEYS.map((k) => (
            <NumberField
              key={k}
              label={LABELS[k]}
              value={s.time_pct[k] ?? 0}
              onChange={(v) => setPath("time_pct", k, v)}
              testId={`setting-time-${k}`}
              suffix="%"
            />
          ))}
        </Grid>
      </Card>

      {/* Booking */}
      <Card title="Booking &amp; deposit">
        <Grid>
          <NumberField
            label="Instant Booking discount (%)"
            value={s.instant_booking_discount_pct}
            onChange={(v) => setS({ ...s, instant_booking_discount_pct: numOrNull(v) })}
            testId="setting-instant-discount"
            suffix="%"
          />
          <NumberField
            label="Deposit percentage"
            value={s.deposit_pct}
            onChange={(v) => setS({ ...s, deposit_pct: numOrNull(v) })}
            testId="setting-deposit-pct"
            suffix="%"
          />
        </Grid>
      </Card>

      <div className="pt-4 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="admin-pricing-save-bottom"
          className="btn-primary disabled:opacity-40"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save changes
        </button>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="border border-white/10 bg-[#0A0A0A]">
      <header className="px-5 py-3 border-b border-white/10">
        <h3 className="text-[11px] tracking-[0.3em] uppercase text-[#E60000] font-bold">{title}</h3>
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>;
}

function MiniLabel({ children }) {
  return (
    <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-1.5">
      {children}
    </div>
  );
}

function NumberField({ label, value, onChange, placeholder, testId, step = "0.01", suffix }) {
  return (
    <label className="block">
      <MiniLabel>{label}</MiniLabel>
      <div className="relative">
        <input
          type="number"
          step={step}
          data-testid={testId}
          value={value ?? ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent border border-white/15 text-white px-4 py-2.5 pr-10 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#E60000] transition-colors"
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">
            {suffix}
          </span>
        )}
      </div>
    </label>
  );
}
